import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ExternalLink, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const SmartSearch = ({ onRouted }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (query) => {
    const text = (query ?? q).trim();
    if (!text) return;
    setBusy(true);
    try {
      const { data } = await api.get("/feed/search", { params: { q: text } });
      if (data.intent === "jobs" && data.links?.length) {
        setResult(data);
      } else if (data.route) {
        toast.success(data.label || "Taking you there");
        setResult(null);
        navigate(data.route);
        onRouted?.();
      } else {
        setResult({ intent: "unknown", suggestions: data.suggestions || [] });
      }
    } catch {
      toast.error("Search failed — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4" data-testid="smart-search">
      <form
        onSubmit={(e) => { e.preventDefault(); run(); }}
        className="flex items-center gap-2 rounded-2xl border border-border bg-card px-4 py-1.5 focus-within:border-primary/50"
      >
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder='Search jobs, customers, opportunities… e.g. "painter jobs near me"'
          className="h-10 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          data-testid="smart-search-input"
          maxLength={120}
        />
        <button type="submit" disabled={busy || !q.trim()}
          className="shrink-0 rounded-full bg-primary px-3.5 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-40"
          data-testid="smart-search-submit">
          {busy ? "…" : "Go"}
        </button>
      </form>

      {result?.intent === "jobs" ? (
        <div className="mt-2 rounded-2xl border border-border bg-card p-4" data-testid="smart-search-results">
          <p className="text-sm font-medium">{result.label}</p>
          <ul className="mt-2 space-y-1.5">
            {result.links.map((l) => (
              <li key={l.name}>
                <a href={l.url} target="_blank" rel="noreferrer"
                   className="flex items-center justify-between rounded-xl border border-border bg-background px-3 py-2 text-sm transition-colors hover:border-primary/40"
                   data-testid={`smart-search-link-${l.name.toLowerCase()}`}>
                  <span>Search on <span className="font-medium">{l.name}</span></span>
                  <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                </a>
              </li>
            ))}
          </ul>
          <button onClick={() => { setResult(null); navigate(result.route); onRouted?.(); }}
            className="mt-2 flex items-center gap-1 text-xs text-primary" data-testid="smart-search-track-link">
            Track your applications <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      ) : null}
      {result?.intent === "unknown" ? (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="smart-search-suggestions">
          {result.suggestions.map((s) => (
            <button key={s} onClick={() => { setQ(s); run(s); }}
              className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground hover:border-primary/40">
              {s}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};
